import React from 'react';
import type { CountdownTimerComponentProps } from './types';
import { ButtonLink } from '../shared/ButtonLink';
import { useCountdownTimer } from './useCountdownTimer';
import { useCountdownColors } from './useCountdownColors';
import { CountdownDisplay } from './CountdownDisplay';
import { BackgroundImage } from './BackgroundImage';
import { renderTextWithLineBreaks } from '../../utils/textFormatting';
import { useBrandFonts } from '../../contexts/CustomColorsContext';
import { getFontFamily } from '../../utils/fontLoader';
import {
  ALIGNMENT_CLASSES,
  SIZE_CLASSES,
  HEADLINE_SIZE_CLASSES,
  SUBHEADLINE_SIZE_CLASSES,
  getBackgroundStyle,
} from './utils';

export const CountdownTimerComponent: React.FC<CountdownTimerComponentProps> = ({
  component,
  isEditing = false,
}) => {
  const { data } = component;
  const { timeRemaining, isExpired } = useCountdownTimer(data.targetDate);
  const { headlineColor, subheadlineColor, numberColor, labelColor, textColor, themeStyles } =
    useCountdownColors(data);
  const brandFonts = useBrandFonts();

  // Resolve values with fallback defaults for resilience against incomplete data
  const size: 'small' | 'medium' | 'large' = data.size || 'medium';
  const alignment: 'left' | 'center' | 'right' = data.alignment || 'center';
  const layout: 'horizontal' | 'vertical' | 'grid' = data.layout || 'horizontal';
  const displayFormat: 'full' | 'compact' | 'minimal' = data.displayFormat || 'full';
  const numberStyle: 'default' | 'bold' | 'card' | 'gradient' = data.numberStyle || 'default';
  const separatorStyle: 'colon' | 'slash' | 'dot' | 'none' = data.separatorStyle || 'colon';
  const showLabels = data.showLabels !== false;
  const headlineSize: 'small' | 'medium' | 'large' = data.headlineSize || 'medium';
  const headlineAlignment: 'left' | 'center' | 'right' = data.headlineAlignment || 'center';
  const units = {
    showDays: data.units?.showDays !== false,
    showHours: data.units?.showHours !== false,
    showMinutes: data.units?.showMinutes !== false,
    showSeconds: data.units?.showSeconds !== false,
  };

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Don't render if expired and hideWhenExpired is true (only in preview/live mode)
  if (isExpired && data.hideWhenExpired && !isEditing) {
    return null;
  }

  const backgroundStyle = getBackgroundStyle(data, themeStyles.countdownTimer?.background);

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.countdownTimer?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.countdownTimer?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div
        className={`flex flex-wrap gap-4 mt-8 ${ALIGNMENT_CLASSES[alignment]} ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block text-center"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  const renderExpiredContent = () => {
    if (!isExpired) return null;

    if (data.hideWhenExpired && !isEditing) {
      return null;
    }

    return (
      <div className={`${ALIGNMENT_CLASSES[alignment]}`}>
        {isEditing && data.hideWhenExpired && (
          <div className="mb-2 px-3 py-1 bg-amber-100 text-amber-800 text-sm rounded-md inline-block">
            Hidden when expired (visible in editor only)
          </div>
        )}
        <p
          className="text-xl md:text-2xl font-semibold"
          style={{ color: textColor, ...bodyFontStyle }}
        >
          {data.expiredMessage}
        </p>
      </div>
    );
  };

  const renderCountdown = () => {
    if (isExpired) {
      return renderExpiredContent();
    }

    return (
      <CountdownDisplay
        timeRemaining={timeRemaining}
        units={units}
        alignment={alignment}
        size={size}
        layout={layout}
        displayFormat={displayFormat}
        numberStyle={numberStyle}
        separatorStyle={separatorStyle}
        showLabels={showLabels}
        numberColor={numberColor}
        labelColor={labelColor}
        labelFontStyle={bodyFontStyle}
      />
    );
  };

  const headlineAlignmentClass =
    headlineAlignment === 'center' ? 'mx-auto' : headlineAlignment === 'right' ? 'ml-auto' : '';

  return (
    <section className={`relative ${SIZE_CLASSES[size]}`} style={backgroundStyle}>
      <BackgroundImage
        enabled={data.backgroundImage?.enabled}
        url={data.backgroundImage?.url}
        cloudfrontUrl={data.backgroundImage?.cloudfrontUrl}
        position={data.backgroundImage?.position}
        size={data.backgroundImage?.size}
        repeat={data.backgroundImage?.repeat}
        parallax={data.backgroundImage?.parallax}
        alt={data.backgroundImage?.alt}
        overlay={data.backgroundImage?.overlay}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`max-w-4xl ${headlineAlignmentClass}`}>
          {/* Headline */}
          {data.showHeadline && data.headline && (
            <h2
              className={`${HEADLINE_SIZE_CLASSES[headlineSize]} font-bold mb-4 ${ALIGNMENT_CLASSES[headlineAlignment]}`}
              style={{ color: headlineColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.headline),
              }}
            />
          )}

          {/* Subheadline */}
          {data.showSubheadline && data.subheadline && (
            <p
              className={`${SUBHEADLINE_SIZE_CLASSES[headlineSize]} mb-8 ${ALIGNMENT_CLASSES[headlineAlignment]}`}
              style={{ color: subheadlineColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.subheadline),
              }}
            />
          )}

          {/* Countdown Timer */}
          <div className="mt-8">{renderCountdown()}</div>

          {/* Call-to-Action Buttons */}
          {renderButtons()}
        </div>
      </div>
    </section>
  );
};

export default CountdownTimerComponent;
